<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'AppNavbar',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref({
    loadingPlaceholder: 'none',
  });

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: { loadingPlaceholder: 'none' },
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const displayTestBadge = computed(() => {
    return _.includes($app.value.document.location.hostname, 'test');
  });
  const displayDevBadge = computed(() => {
    return _.includes($app.value.document.location.hostname, 'local');
  });

  const act_3868587809308211_script_wfxrt3qne_value_callable = async (
    $event
  ) => {
    return $app.value.toggleConfirmPageClose(!$app.value.confirmPageClose);
  };
  const act_3868587809308211_script_wfxrt3qne_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3868587809308211_script_wfxrt3qne_value_callable($event);
      },
    };
  };
  const act_6729735210012935_logout_6fvp9hkl0_getActionArgs = async (
    $event
  ) => {
    return {};
  };
  const act_3396695947931692_showModal_mcatd6m0t_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'AboutModal',
    };
  };

  const act_7315092382398562_reloadComponentData_npv92p8op =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_zsezxs4iu =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_npv92p8op,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_zsezxs4iu],
    event: 'reload',
  });
  const act_3868587809308211_script_wfxrt3qne = new actionTypes.ScriptAction({
    actionArgs: act_3868587809308211_script_wfxrt3qne_getActionArgs,
  });
  const inst_evh_3868587809308211_updateModelValue_azy0kz7z9 =
    new actionTypes.EventHandlerImpl({
      action: act_3868587809308211_script_wfxrt3qne,
      event: 'update:modelValue',
      displayName: 'script',
    });
  const inst_evh_3868587809308211_updateModelValue =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3868587809308211_updateModelValue_azy0kz7z9],
      event: 'update:modelValue',
    });
  const act_6729735210012935_logout_6fvp9hkl0 = new actionTypes.LogoutAction({
    actionArgs: act_6729735210012935_logout_6fvp9hkl0_getActionArgs,
  });
  const inst_evh_6729735210012935_clickPrevent_davwsl7bo =
    new actionTypes.EventHandlerImpl({
      action: act_6729735210012935_logout_6fvp9hkl0,
      event: 'click.prevent',
      displayName: 'logout',
    });
  const inst_evh_6729735210012935_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6729735210012935_clickPrevent_davwsl7bo],
      event: 'click.prevent',
    });
  const act_3396695947931692_showModal_mcatd6m0t =
    new actionTypes.ShowModalAction({
      actionArgs: act_3396695947931692_showModal_mcatd6m0t_getActionArgs,
    });
  const inst_evh_3396695947931692_clickPrevent_t78ak6xdm =
    new actionTypes.EventHandlerImpl({
      action: act_3396695947931692_showModal_mcatd6m0t,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_3396695947931692_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3396695947931692_clickPrevent_t78ak6xdm],
      event: 'click.prevent',
    });

  const actions = {
    inst_evh_7315092382398562_reload_zsezxs4iu,
    inst_evh_7315092382398562_reload,
    inst_evh_3868587809308211_updateModelValue_azy0kz7z9,
    inst_evh_3868587809308211_updateModelValue,
    inst_evh_6729735210012935_clickPrevent_davwsl7bo,
    inst_evh_6729735210012935_clickPrevent,
    inst_evh_3396695947931692_clickPrevent_t78ak6xdm,
    inst_evh_3396695947931692_clickPrevent,
  };

  const evh_7315092382398562_reload_zsezxs4iu = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_zsezxs4iu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3868587809308211_updateModelValue_azy0kz7z9 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3868587809308211_updateModelValue_azy0kz7z9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3868587809308211_updateModelValue = async (event, scope) => {
    return await actions.inst_evh_3868587809308211_updateModelValue.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6729735210012935_clickPrevent_davwsl7bo = async (event, scope) => {
    return await actions.inst_evh_6729735210012935_clickPrevent_davwsl7bo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6729735210012935_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_6729735210012935_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3396695947931692_clickPrevent_t78ak6xdm = async (event, scope) => {
    return await actions.inst_evh_3396695947931692_clickPrevent_t78ak6xdm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3396695947931692_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_3396695947931692_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    loadingPlaceholder="none"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <BNavbar type="light" variant="default" class="app-navbar">
      <AppDropdownMainMenu v-if="$app.layoutMode === 'sidebar'">
      </AppDropdownMainMenu>
      <BNavbarBrand to="/">
        {{ $app.spec.brand }}
        <ItpBadge v-if="displayTestBadge" variant="danger" class="mx-2"
          >Test</ItpBadge
        >
        <ItpBadge v-if="displayDevBadge" variant="warning" class="mx-2"
          >Dev</ItpBadge
        >
      </BNavbarBrand>
      <AppTabbedMainMenu v-if="$app.layoutMode === 'tabbed'">
      </AppTabbedMainMenu>
      <BCollapse is-nav>
        <BNavbarNav class="ms-auto">
          <BNavItemDropdown right class="mx-3">
            <template #button-content>
              <ItpIcon name="fa-user-circle" class="me-1"> </ItpIcon>
              <span>
                {{ $app.auth.user.fullName || $app.auth.user.userName }}
              </span>
            </template>
            <ItpNavItem
              :href="$app.auth.accountUrl"
              target="_blank"
              icon="fa-user-circle"
              :text="pgettext('MainMenu', 'Profile')"
              dropdown-item
            >
            </ItpNavItem>
            <ItpNavItem icon="fa-globe">
              <ItpLocaleSelector class="nav-item-content"> </ItpLocaleSelector>
            </ItpNavItem>
            <ItpNavItem icon="fa-palette">
              <ItpLayoutModeSelector class="nav-item-content">
              </ItpLayoutModeSelector>
            </ItpNavItem>
            <BDropdownDivider> </BDropdownDivider>
            <ItpNavItem icon="mdi:check-circle-outline">
              <BFormCheckbox
                :modelValue="$app.confirmPageClose"
                switch
                class="nav-item-content"
                @update:modelValue="
                  evh_3868587809308211_updateModelValue($event, {})
                "
              >
                <ItpText
                  :text="pgettext('MainMenu', 'Confirm page close')"
                  class="user-select-none"
                >
                </ItpText>
              </BFormCheckbox>
            </ItpNavItem>
            <ItpNavItem
              href="#"
              icon="fa-sign-out-alt"
              :text="pgettext('MainMenu', 'Sign out')"
              dropdown-item
              @click.prevent="evh_6729735210012935_clickPrevent($event, {})"
            >
            </ItpNavItem>
          </BNavItemDropdown>
          <BNavItemDropdown right>
            <template #button-content>
              <ItpIcon name="fa-bars"> </ItpIcon>
            </template>
            <ItpNavItem
              dropdown-item
              to="/jobs-v2"
              icon="fa-cogs"
              :text="pgettext('MainMenu', 'Jobs')"
            >
            </ItpNavItem>
            <ItpNavItem
              dropdown-item
              to="/attachments"
              :icon="$config.attachment.icon"
              :text="pgettext('MainMenu', 'Attachments')"
            >
            </ItpNavItem>
            <ItpNavItem
              dropdown-item
              to="/templates"
              icon="fa-file-invoice"
              :text="pgettext('MainMenu', 'Templates')"
            >
            </ItpNavItem>
            <BDropdownDivider> </BDropdownDivider>
            <ItpNavItem
              dropdown-item
              to="/settings"
              icon="fa-cog"
              :text="pgettext('MainMenu', 'Settings')"
            >
            </ItpNavItem>
            <ItpNavItem
              href="#"
              icon="fa-question-circle"
              :text="pgettext('MainMenu', 'About Integro')"
              dropdown-item
              @click.prevent="evh_3396695947931692_clickPrevent($event, {})"
            >
            </ItpNavItem>
          </BNavItemDropdown>
        </BNavbarNav>
      </BCollapse>
    </BNavbar>
  </ItpUsercontrol>
</template>
